import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { memo, useCallback, useEffect, useRef, useState } from 'react'
import { sendRoomMessage } from '../../../../../actions/sendRoomMessage'
import { openWhiteboardDialog } from '../../../../../app-state/modals/state.dialog'
import { EMOJIS, MENU } from '../../../../../constants/dialogs'
import { TIPS } from '../../../../../constants/drawer'
import { VideoType } from '../../../../../constants/enums'
import { getPercentage } from '../../../../../utilities/dimensions'
import i18n from '../../../../../utilities/i18n'
import ButtonShare from '../../../../Buttons/ButtonShare'
import {
    useDialogSelector,
    useDrawerSelector,
} from '../../../../Hooks/useModal'
import { usePortalStateSelector } from '../../../../Hooks/usePortalState'
import { useUserSelector } from '../../../../Hooks/useUser'
import APP_BREAKPOINTS, { minPinWidth } from '../../../../styles/breakpoints'
import { palette } from '../../../../styles/colors'
import shape from '../../../../styles/shape'
import {
    COMPONENT_HEIGHTS,
    PAPER_HEIGHTS,
    PINNED_ROOM_CHAT_MIN_WIDTH,
    PINNED_ROOM_MAX_HEIGHT,
} from '../../../../theme'
import LiveSnapStream from '../../../LiveSnapStream'
import ChatHeader from '../../ChatHeader'
import Compose from '../../Compose'
import BackgroundFade from '../BackgroundFade'
import Chat from '../Chat'

const rootStyles = css({
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
})
const PortalGridRoot = styled('div')({
    position: 'relative',
    display: 'flex',
    flexFlow: 'row nowrap',
    overflow: 'hidden',
    height: '100%',
})
const VideoGrid = styled('div')({
    display: 'flex',
    flexDirection: 'column',
})
const ChatGrid = styled('div')({
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflowX: 'hidden',
    overflowY: 'hidden',
    zIndex: PAPER_HEIGHTS.zero,
    background: palette.backgroundGrid,
    '&::-webkit-scrollbar': {
        display: 'none',
    },
})
const chatPaneStyles = css({
    display: 'flex',
    alignItems: 'flex-end',
    flexDirection: 'column',
    height: '100%',
})
const offlineButtonsStyles = css({
    position: 'absolute',
    display: 'flex',
    flexFlow: 'row',
    justifyContent: 'flex-start',
    alignItems: 'flex-end',
    padding: 0,
    bottom: 0,
    width: '100%',
    height: '100%',
    [minPinWidth(APP_BREAKPOINTS.md)]: {
        justifyContent: 'flex-end',
        alignItems: 'flex-start',
    },
})

const streamContainerStyles = css({
    position: 'relative',
    display: 'flex',
    height: '100%',
    width: '100%',
    flexGrow: 0,
    background: '#090909',
    borderBottomLeftRadius: `${shape.borderRadius}px`,
    '& video': {
        borderBottomLeftRadius: `${shape.borderRadius}px`,
        objectFit: 'revert !important' as any,
    },
})

const chatlessStreamContainerStyles = css([
    streamContainerStyles,
    {
        borderBottomLeftRadius: `${shape.borderRadius}px`,
        borderBottomRightRadius: `${shape.borderRadius}px`,
        '& video': {
            borderBottomLeftRadius: `${shape.borderRadius}px`,
            borderBottomRightRadius: `${shape.borderRadius}px`,
            objectFit: 'revert !important' as any,
        },
    },
])

const getRightStyles = ({ videoWidth, width, showChat, isModel }) => {
    if (showChat && isModel)
        return `${100 - getPercentage(videoWidth, width)}%` as const
    return null
}

const getLeftStyles = ({ videoWidth, width, showChat, isModel }) => {
    if (!showChat) return '100%' as const
    if (isModel) return `${getPercentage(videoWidth, width)}%` as const
    return 'auto' as const
}

type PinnedPortalProps = {
    id: number
    width: number
    showChat: boolean
    isMobile: boolean
    darkMode: boolean
    loading?: boolean
}

const PinnedPortal = memo((props: PinnedPortalProps) => {
    const { id, width, loading, darkMode, isMobile, showChat } = props

    const banned = useUserSelector(id, 'banned')
    const isBroadcasting = useUserSelector(id, 'isBroadcasting')
    const isModel = useUserSelector(id, 'isModel')
    const isOffline = useUserSelector(id, 'isOffline')
    const slug = useUserSelector(id, 'slug')
    const isAway = useUserSelector(id, 'isAway')
    const channel = useUserSelector(id, 'channel')
    const isLounge = useUserSelector(id, 'isLounge')
    const isClubShow = useUserSelector(id, 'isClubShow')
    const isGroupShow = useUserSelector(id, 'isGroupShow')
    const isNGVideo = useUserSelector(id, 'isNGVideo')
    const isTruePrivate = useUserSelector(id, 'isTruePrivate')
    const phase = useUserSelector(id, 'phase')
    const isPrivateShow = useUserSelector(id, 'isPrivateShow')
    const server = useUserSelector(id, 'server')
    const isKicked = useUserSelector(id, 'isKicked')

    const composeFocused = usePortalStateSelector('composeFocused')

    const videoWidth = showChat ? width - PINNED_ROOM_CHAT_MIN_WIDTH : width
    const videoHeight = Math.min(
        (videoWidth * 9) / 16,
        isMobile ? PINNED_ROOM_MAX_HEIGHT / 2 : PINNED_ROOM_MAX_HEIGHT,
    )
    const roomHeight = videoHeight + COMPONENT_HEIGHTS.chatHeader

    const isDialogOpen = useDialogSelector('show')
    const dialogID = useDialogSelector('type')
    const isDrawerOpen = useDrawerSelector('show')
    const drawerID = useDrawerSelector('type')
    const isMenuOpen = isDialogOpen && dialogID === MENU
    const isTipDrawerOpen = isDrawerOpen && drawerID === TIPS
    const isEmojiDialogOpen = isDialogOpen && dialogID === EMOJIS
    const disableKeyboardOpenDetection =
        isMenuOpen || isTipDrawerOpen || isEmojiDialogOpen

    const containerRef = useRef<HTMLDivElement>(null)
    const verticalActionBarContainerRef = useRef<HTMLDivElement>(null)

    const onOpenWhiteboard = useCallback(() => {
        openWhiteboardDialog(slug)
    }, [slug])

    useEffect(() => {
        if (!composeFocused && containerRef.current) {
            containerRef.current.scrollTop = 0
        }
    }, [composeFocused])

    const leftStyles = getLeftStyles({
        videoWidth,
        width,
        showChat,
        isModel,
    })

    const rightStyles = getRightStyles({
        videoWidth,
        width,
        showChat,
        isModel,
    })

    const [viewingTips, setViewingTips] = useState(false)
    const toggleTips = useCallback(() => setViewingTips((value) => !value), [])

    if (!id) return null
    // console.log('[PinnedPortal]', slug, {
    //     roomHeight,
    //     width,
    //     videoWidth,
    //     videoHeight,
    // })

    return (
        <div css={rootStyles} style={{ width, height: roomHeight }}>
            <BackgroundFade
                id={id}
                width={videoWidth}
                height={videoHeight}
                top={28}
                filter={
                    darkMode
                        ? 'blur(26px) saturate(150%) opacity(45%) brightness(105%)'
                        : 'blur(26px) saturate(150%) opacity(40%) brightness(165%)'
                }
            />
            <PortalGridRoot>
                {/* [Video Section] we're in a model's room or one of the lounges */}
                {isModel && (
                    <VideoGrid
                        style={{
                            maxWidth: leftStyles === 'auto' ? null : leftStyles,
                            flexBasis: leftStyles,
                            flexShrink: 0,
                        }}
                        data-testid="Video__Section"
                    >
                        <ChatHeader id={id} pinned />
                        {/* in a model's room and allowed to view her stream or in the lounge */}
                        {!banned && !isKicked && (
                            <div
                                css={
                                    showChat
                                        ? streamContainerStyles
                                        : chatlessStreamContainerStyles
                                }
                            >
                                {isOffline && (
                                    <div css={offlineButtonsStyles}>
                                        <ButtonShare slug={slug} />
                                    </div>
                                )}
                                <LiveSnapStream
                                    videoMuted
                                    id={id}
                                    width={videoWidth}
                                    height={videoHeight}
                                    isTruePrivate={isTruePrivate}
                                    isBroadcasting={isBroadcasting}
                                    isPrivateShow={isPrivateShow}
                                    isNGVideo={isNGVideo}
                                    isGroupShow={isGroupShow}
                                    isClubShow={isClubShow}
                                    isOffline={isOffline}
                                    channel={channel}
                                    banned={banned}
                                    server={server}
                                    isLounge={isLounge}
                                    isModel={isModel}
                                    phase={phase}
                                    isAway={isAway}
                                    videoID={VideoType.PinnedRoom}
                                    key={`pinned-room-snap-stream-${slug}`}
                                />
                            </div>
                        )}
                    </VideoGrid>
                )}
                {/* [Chat Section] */}
                {showChat && (
                    <ChatGrid
                        ref={containerRef}
                        style={{
                            flexBasis: rightStyles,
                            maxWidth: rightStyles,
                            overflowX: 'hidden',
                            overflowY: 'hidden',
                        }}
                        data-testid="Chat__Section"
                    >
                        <div
                            css={chatPaneStyles}
                            data-testid="Chat__Container"
                            ref={verticalActionBarContainerRef}
                        >
                            <Chat
                                onOpenWhiteboard={onOpenWhiteboard}
                                id={id}
                                height={roomHeight}
                                pinned
                                showingTips={viewingTips}
                                onTipToggle={toggleTips}
                                pm={false}
                            />
                            <Compose
                                hide={false}
                                disableKeyboardOpenDetection={
                                    disableKeyboardOpenDetection
                                }
                                noFullWindowButtons
                                onSend={sendRoomMessage}
                                id={id}
                                placeholder={i18n.t('chat.sendChatMessage')}
                                disabled={loading}
                                hideTipButton
                            />
                        </div>
                    </ChatGrid>
                )}
                {/* </Grid>*/}
            </PortalGridRoot>
        </div>
    )
})

export default PinnedPortal
